import { Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LegalPage from '../../components/common/LegalPage';
import { PATHS } from '../../routes';

// Content from New Website.docx, "Page Title 'Privacy Policy'".
export default function PrivacyPolicy() {
  return (
    <LegalPage title="Website Privacy Notice">
      <Typography variant="body1">
        Cherry Creek Mortgage, LLC, and its affiliates (collectively &ldquo;Cherry Creek Mortgage, LLC&rdquo; or
        &ldquo;We&rdquo;) will treat personal data that you provide through this website or any related websites
        (collectively the &ldquo;Site&rdquo;) according to this privacy policy (this &ldquo;Policy&rdquo;). This
        Policy is consistent with our treatment of personal data that you provide offline to us or to our brokers
        and dealers. By visiting the Site, you agree to be bound by this Policy.
      </Typography>

      <Typography variant="h5">Foreign Visitors</Typography>
      <Typography variant="body1">
        If you are visiting the Site from outside the United States, please do not provide any personal data
        through the Site or by e-mail, as this Policy may not comply with local laws in your jurisdiction.
      </Typography>

      <Typography variant="h5">Children</Typography>
      <Typography variant="body1">
        This Site is not intended for visitors under 18 years of age. We do not knowingly solicit or collect
        personal data from or about individuals under 18 years of age, nor do we knowingly market our products or
        services to individuals under 18 years of age.
      </Typography>

      <Typography variant="h5">Personal Data We Collect from Visitors</Typography>
      <Typography variant="body1">
        &ldquo;Personal data&rdquo; identifies an individual, such as name, address, telephone number, or e-mail
        address. We may collect the following types of personal data:
      </Typography>
      <Typography variant="body1" component="ul">
        <li>If you apply for a job: your name, address, references, and employment history.</li>
        <li>
          If you apply for credit: your name, address, Social Security number, credit history, and financial
          information, directly or through one of our brokers or dealers.
        </li>
        <li>
          If you use the Site to check the status of your account: your name, account number, and other
          verification information, such as a password.
        </li>
        <li>If you send us e-mail or request information: your name, e-mail address, and any other personal data you include in your message.</li>
      </Typography>

      <Typography variant="h5">Other Data We Automatically Collect</Typography>
      <Typography variant="body1">
        When you visit the Site, we may collect certain nonpersonal data by various means, including the use of
        &ldquo;cookies.&rdquo; A cookie is an alphanumeric string of text (a unique identifier) that our web server
        stores on your computer through your web browser. Cookies allow us to recognize your device, improve Site
        functionality, analyze aggregate traffic, and maintain relevant content. You may configure your browser to
        reject cookies; however, doing so may disable certain Site features. Cookies do not contain personal data
        and cannot read or transmit information from your computer&rsquo;s hard drive. They may be combined with
        personal data you voluntarily provide only with your consent. Web browsers also transmit IP addresses so
        that web servers know where to deliver requested pages. IP addresses are analyzed only in rare
        circumstances, such as investigating security issues.
      </Typography>

      <Typography variant="h5">How We Use the Data We Collect</Typography>
      <Typography variant="body1">We may use your personal data to:</Typography>
      <Typography variant="body1" component="ul">
        <li>Consider employment applications</li>
        <li>Evaluate creditworthiness and process applications</li>
        <li>Respond to account inquiries and information requests</li>
        <li>Improve Site content, products, services, and marketing efforts</li>
        <li>Contact you about products or services that may be of interest</li>
      </Typography>
      <Typography variant="body1">
        Where required by law, marketing communications will include opt-out instructions. Account-related and
        administrative communications are not subject to opt-out.
      </Typography>

      <Typography variant="h5">Sharing Data</Typography>
      <Typography variant="body1">
        <strong>Affiliates:</strong> We may share personal data with our affiliates for servicing, marketing, or
        informational purposes.
      </Typography>
      <Typography variant="body1">
        <strong>Agents and Service Providers:</strong> We may disclose personal data to service providers that
        perform services on our behalf, including account management, credit evaluation, customer service,
        marketing, research, and employment processing.
      </Typography>
      <Typography variant="body1">
        <strong>Third Parties (Disclosures Permitted or Required by Law):</strong> We may disclose personal data as
        permitted or required by law, including for credit reporting, regulatory compliance, fraud prevention,
        legal enforcement, or business transactions.
      </Typography>
      <Typography variant="body1">
        <strong>Business Transfers:</strong> Personal data may be transferred as part of a merger, acquisition,
        asset sale, or corporate reorganization.
      </Typography>
      <Typography variant="body1">
        <strong>Other Third Parties (Optional):</strong> As permitted by law, personal data may be shared with
        non-affiliated third parties for marketing or service offerings that may be of interest to you.
      </Typography>
      <Typography variant="body1">
        <strong>Consumer Opt-Out Rights:</strong> Consumer customers may have the right, under applicable law, to
        restrict certain data sharing. To submit an opt-out request, please visit our{' '}
        <RouterLink to={PATHS.optOut}>Opt-Out page</RouterLink>.
      </Typography>
      <Typography variant="body1">
        <strong>Security:</strong> Cherry Creek Mortgage, LLC uses reasonable safeguards to protect personal data.
        Online forms and certain transmissions are encrypted. Email is not encrypted, and sensitive personal data
        should not be sent via email. We do not request personal information through unsolicited email.
      </Typography>
      <Typography variant="body1">
        <strong>Public Areas:</strong> We are not responsible for information you choose to share in public areas
        of the Site. Such information may be collected and used by others.
      </Typography>

      <Typography variant="h5">Protecting Your Identity and Credit</Typography>
      <Typography variant="body1">
        You may opt out of prescreened credit offers by calling (888) 567-8688 or visiting{' '}
        <a href="https://www.optoutprescreen.com" target="_blank" rel="noopener noreferrer">
          www.optoutprescreen.com
        </a>
        .
      </Typography>

      <Typography variant="h5">California Privacy Rights</Typography>
      <Typography variant="body1">
        California residents may have additional rights regarding personal information under applicable law,
        including the right to request information about certain disclosures and to exercise data rights where
        applicable.
      </Typography>

      <Typography variant="h5">Links to Other Websites</Typography>
      <Typography variant="body1">
        The Site may contain links to third-party websites. We are not responsible for their content or privacy
        practices.
      </Typography>

      <Typography variant="h5">Notices and Revisions</Typography>
      <Typography variant="body1">
        This Policy may be updated from time to time. Continued use of the Site constitutes acceptance of the
        Policy in effect at that time.
      </Typography>
    </LegalPage>
  );
}
